/**
 * Deterministic column type inference: numeric / text / categorical.
 *
 * Spec "Type Inference" (PRODUCT.md), with the locked open-decision defaults:
 *   - numeric: ≥80% of the values THAT CARRY DATA numeric (blanks/null-likes sit
 *     outside the denominator — F13/F28: a sparse event-amount column blank on
 *     most snapshot rows still infers numeric); non-numeric exception tokens are
 *     surfaced as "treated as null", never silently nulled.
 *   - text/categorical: single boundary at 90% uniqueness (≥90% unique → text,
 *     else categorical) — collapses the spec's 80–90% gap per the locked default.
 *
 * The function consumes a streaming accumulator, not a materialized array, so the
 * client's streaming parse feeds it row-by-row without holding ~1M values in
 * memory, and the server re-runs it the same way over the scrubbed file. A full
 * scan (not sampling) is required: the numeric rule demands that EVERY
 * non-numeric value be null-like, which a sample cannot guarantee. Memory is
 * bounded by the caps below regardless of row count.
 */

import { isDateValue } from './date-parse';
import { isNullLike } from './null-like';
import { parseNumericValue, parseNumericValueDetailed } from './numeric-parse';
import type { NumericSubtype } from './types';

/** Cap on tracked distinct values (uniqueness); bounds memory at high cardinality. */
export const UNIQUENESS_DISTINCT_CAP = 200_000;
/** Cap on distinct non-fitting values surfaced for the numeric "treated as null" display. */
export const NON_FITTING_SAMPLE_CAP = 100;
/**
 * Minimum share of a numeric column's parsed values that must carry a decoration
 * (a `%` for percentage, a currency symbol/code for currency) before the column's
 * display subtype is inferred as that decoration. A stray `$` in an otherwise plain
 * column never flips it. Display-only, so this is a soft heuristic the user overrides.
 */
export const SUBTYPE_MIN_SHARE = 0.25;

export interface ColumnInferenceConfig {
  /** Minimum numeric fraction of NON-NULL values for a numeric classification. */
  readonly numericMinRatio: number;
  /** Minimum date/period fraction of NON-NULL values for a date classification. */
  readonly dateMinRatio: number;
  /** Minimum uniqueness fraction (of non-null values) for a text classification. */
  readonly textMinUniqueness: number;
}

export const DEFAULT_INFERENCE_CONFIG: ColumnInferenceConfig = {
  numericMinRatio: 0.8,
  dateMinRatio: 0.8,
  textMinUniqueness: 0.9,
};

/**
 * Streaming accumulator for one column. Feed every raw cell value through
 * {@link observeValue}, then call {@link inferColumnType}.
 */
export interface ColumnStatsAccumulator {
  total: number;
  numericCount: number;
  /** Count of non-blank values that parse as a date/period (see {@link isDateValue}). */
  dateCount: number;
  nullLikeCount: number;
  /** Distinct non-null-like values (for uniqueness), capped. */
  readonly distinct: Set<string>;
  distinctCapExceeded: boolean;
  /** Distinct non-numeric raw values for the numeric non-fitting display, capped. */
  readonly nonNumericSamples: Set<string>;
  /** True count of non-numeric values (≥ nonNumericSamples.size). */
  nonNumericTotal: number;
  /** Of the numeric values, how many carried a literal `%` (drives percentage subtype). */
  percentMarkedCount: number;
  /** Of the numeric values, how many carried a currency symbol/code (drives currency subtype). */
  currencyMarkedCount: number;
  /**
   * Maximum PARSED numeric value observed (a `5%` cell contributes 5 — the stored
   * canonical parse is the bare numeral, decorations stripped), or null when no
   * value parsed numeric. Feeds {@link defaultPercentVariant}, the smart default
   * between the two percent variants: a max over 1 means percent-points (8.36 =
   * 8.36%), a max at or below 1 means the fraction variant.
   */
  numericMax: number | null;
}

export function createColumnAccumulator(): ColumnStatsAccumulator {
  return {
    total: 0,
    numericCount: 0,
    dateCount: 0,
    nullLikeCount: 0,
    distinct: new Set<string>(),
    distinctCapExceeded: false,
    nonNumericSamples: new Set<string>(),
    nonNumericTotal: 0,
    percentMarkedCount: 0,
    currencyMarkedCount: 0,
    numericMax: null,
  };
}

/**
 * True when a raw value fits the one canonical numeric grammar
 * ({@link parseNumericValue}). Kept as a thin predicate over that single parser so
 * inference, ingest, and the SQL aggregate can never disagree about "is this a
 * number" (the drift the `fact-inference` PARITY NOTE used to document). See
 * {@link ./numeric-parse} and {@link ./parsing-rules} for the exact rules.
 */
export function isNumericValue(raw: string): boolean {
  return parseNumericValue(raw) !== null;
}

/** Observe one raw cell value. O(1) amortized; safe to call ~30M times. */
export function observeValue(acc: ColumnStatsAccumulator, raw: string): void {
  acc.total += 1;

  const nullLike = isNullLike(raw);
  if (nullLike) {
    acc.nullLikeCount += 1;
  }

  const parsed = nullLike ? null : parseNumericValueDetailed(raw);
  const numeric = parsed !== null && parsed.value !== null;
  if (numeric) {
    acc.numericCount += 1;
    if (parsed.hadPercent) acc.percentMarkedCount += 1;
    if (parsed.hadCurrency) acc.currencyMarkedCount += 1;
    if (acc.numericMax === null || parsed.value > acc.numericMax) {
      acc.numericMax = parsed.value;
    }
  } else {
    acc.nonNumericTotal += 1;
    if (acc.nonNumericSamples.size < NON_FITTING_SAMPLE_CAP) {
      acc.nonNumericSamples.add(raw);
    }
  }

  // Date/period parse is independent of the numeric parse: a YYYYMM period code is
  // BOTH numeric and a date (inferColumnType gives date precedence). Mirrors the DB
  // aggregate's `count(*) filter (where dateOrdinal is not null and not isBlankLike)`.
  if (!nullLike && isDateValue(raw)) {
    acc.dateCount += 1;
  }

  // Uniqueness is measured over non-null-like values only.
  if (!nullLike && !acc.distinctCapExceeded) {
    if (!acc.distinct.has(raw)) {
      if (acc.distinct.size < UNIQUENESS_DISTINCT_CAP) {
        acc.distinct.add(raw);
      } else {
        acc.distinctCapExceeded = true;
      }
    }
  }
}

/** The inferred classification of a column (discriminated union). */
export type ColumnInference =
  | {
    readonly type: 'numeric';
    readonly numericRatio: number;
    /** Values that don't fit numeric and will be treated as null (UI display). */
    readonly nonFittingValues: readonly string[];
    /** True count (≥ nonFittingValues.length when capped). */
    readonly nonFittingTotal: number;
  }
  | {
    readonly type: 'date';
    /** Fraction of NON-NULL values that parsed as a date/period (same denominator as the gate). */
    readonly dateRatio: number;
    /** Non-date values treated as exceptions (kept empty by the DB aggregate path). */
    readonly nonFittingValues: readonly string[];
    /** True count of non-date values (total − dateCount). */
    readonly nonFittingTotal: number;
  }
  | {
    readonly type: 'text';
    readonly uniquenessRatio: number;
    /** True when the distinct-value cap was hit and uniqueness is a lower bound. */
    readonly uniquenessApproximate: boolean;
  }
  | {
    readonly type: 'categorical';
    readonly uniquenessRatio: number;
  };

/** Classify a column from its accumulated stats. Pure; exact per spec. */
export function inferColumnType(
  acc: ColumnStatsAccumulator,
  config: ColumnInferenceConfig = DEFAULT_INFERENCE_CONFIG,
): ColumnInference {
  const { total, numericCount, dateCount, nullLikeCount } = acc;
  const nonNull = total - nullLikeCount;

  // Empty or all-null column: no signal. Categorical is the conservative
  // non-text default and never makes a numeric Standard field usable.
  if (total === 0 || nonNull === 0) {
    return { type: 'categorical', uniquenessRatio: 0 };
  }

  // Date precedence: a column whose values are dominantly dates / period codes is a
  // DATE, even though period codes (e.g. YYYYMM "202401") also parse numeric. Leftover
  // non-date values are tolerated as exceptions (same ≥ratio rule as numeric below).
  // The ratio is judged over the NON-NULL values (blanks = "unknown" never dilute a
  // classification — F13/F28): a sparse event-date column blank on most rows still
  // infers date when its populated cells are dominantly dates. The reported dateRatio
  // uses the SAME denominator as the gate, so display and decision can't contradict.
  if (dateCount / nonNull >= config.dateMinRatio) {
    return {
      type: 'date',
      dateRatio: dateCount / nonNull,
      nonFittingValues: [],
      nonFittingTotal: total - dateCount,
    };
  }

  // Numeric: ≥80% of the NON-NULL values numeric (blanks never dilute — F13/F28: a
  // charge_off_amount column blank on ~98% of snapshot rows still infers numeric). A
  // field stays numeric even when a few non-null-like, non-numeric EXCEPTION tokens
  // ("Unknown", "Pending") are present — those are surfaced through nonFittingValues
  // ("treated as null"), never a reason to demote the field to categorical. (This
  // deliberately drops the old strict every-non-numeric-is-null-like gate so messy
  // numeric columns keep their type.) The reported numericRatio shares the gate's
  // non-null denominator — a 1.5%-populated all-numeric column reads ~1.0, not ~0.015.
  if (numericCount / nonNull >= config.numericMinRatio) {
    return {
      type: 'numeric',
      numericRatio: numericCount / nonNull,
      nonFittingValues: [...acc.nonNumericSamples],
      nonFittingTotal: acc.nonNumericTotal,
    };
  }

  // Text vs categorical by uniqueness over non-null values, single 90% boundary.
  if (acc.distinctCapExceeded) {
    // distinct is a lower bound; only provably text when even the lower bound
    // clears the threshold. Otherwise default to text (safe — no field set
    // requires a text-typed field) and flag the approximation.
    const lowerBound = acc.distinct.size / nonNull;
    return {
      type: 'text',
      uniquenessRatio: lowerBound,
      uniquenessApproximate: lowerBound < config.textMinUniqueness,
    };
  }

  const uniqueness = acc.distinct.size / nonNull;
  if (uniqueness >= config.textMinUniqueness) {
    return { type: 'text', uniquenessRatio: uniqueness, uniquenessApproximate: false };
  }
  return { type: 'categorical', uniquenessRatio: uniqueness };
}

/**
 * The subtype suggested for a numeric column, from how its values were decorated:
 * a percent variant when a meaningful share carried a `%`, else `currency` when a
 * meaningful share carried a currency symbol/code, else `number`. The `%` decoration
 * is now STRIPPED, not rescaled, so a %-decorated column stores the bare numerals.
 *
 * Which percent variant (R4, PRODUCT.md "Numeric subtypes"):
 *  - FULLY `%`-decorated (`percentMarkedCount === numericCount`): the column is
 *    already written in percent points — a literal `%` on every value declares
 *    "this numeral IS the percentage" — so the default is `percent_points` at ANY
 *    magnitude. A `0.25%` cell must read as 0.25%, never be rescaled to 25%.
 *  - PARTIALLY decorated (the branch still fires at pctShare ≥
 *    {@link SUBTYPE_MIN_SHARE}): a column of otherwise bare numerals with some
 *    `%` marks still leans on magnitude via {@link defaultPercentVariant} (a max
 *    over 1 ⇒ `percent_points`, a max ≤ 1 ⇒ the fraction `percentage`).
 *
 * This drives the DEFAULT only: the user reviews and can override it, and the
 * chosen variant applies the authoritative percent scaling at projection. Call
 * only for a column already inferred numeric (returns `number` when there are no
 * numeric values to judge).
 */
export function inferNumericSubtype(acc: ColumnStatsAccumulator): NumericSubtype {
  if (acc.numericCount === 0) return 'number';
  const pctShare = acc.percentMarkedCount / acc.numericCount;
  const curShare = acc.currencyMarkedCount / acc.numericCount;
  if (pctShare >= SUBTYPE_MIN_SHARE && pctShare >= curShare) {
    // R4: every numeric value carries a literal `%` — the column is declared
    // percent-points by its own decoration, regardless of magnitude.
    return acc.percentMarkedCount === acc.numericCount
      ? 'percent_points'
      : defaultPercentVariant(acc.numericMax);
  }
  if (curShare >= SUBTYPE_MIN_SHARE) return 'currency';
  return 'number';
}

/**
 * The percent display variant a field's stored values imply, for defaulting when
 * the user picks a percent format for an UNDECORATED column: a max stored value
 * over 1 would read as more than 100% under the fraction variant — those columns
 * almost certainly hold percent-points (an 8.36 meaning 8.36%) — while a max ≤ 1
 * (or an unknown max) defaults to the fraction variant, matching what %-decorated
 * columns store. A soft display-only hint: the user can always pick the other
 * variant, and either way storage is untouched.
 */
export function defaultPercentVariant(
  numericMax: number | null,
): Extract<NumericSubtype, 'percentage' | 'percent_points'> {
  return numericMax !== null && numericMax > 1 ? 'percent_points' : 'percentage';
}
